import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed  disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary-600 text-primary-foreground enabled:hover:bg-primary-600/90',
        destructive:
          'bg-red-600 text-primary-foreground enabled:hover:bg-red-500/90 dark:bg-red-900 dark:text-primary-foreground dark:enabled:hover:bg-red-900/90',
        outline:
          'border border-primary-600 bg-transparent text-primary-600 shadow-sm enabled:hover:bg-accent enabled:hover:text-accent-foreground',
        secondary:
          'bg-secondary text-gray-900 enabled:hover:bg-secondary/80 dark:bg-gray-800 dark:text-primary-foreground dark:enabled:hover:bg-gray-800/80',
        ghost:
          'enabled:hover:bg-secondary enabled:hover:text-gray-900 dark:enabled:hover:bg-gray-800 dark:enabled:hover:text-primary-foreground',
        link: 'text-gray-900 underline-offset-4 enabled:hover:underline dark:text-primary-foreground',
        success:
          'bg-primary-600 text-secondary enabled:hover:bg-primary-600/90 ',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
